import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskLines, Eyebrow } from "../site/Reveal";
import { LogoMark } from "../site/Logo";

const STATS = [
  ["10+", "Years in capital-market compliance"],
  ["06", "SEBI-regulated verticals served"],
];

const SealBadge = () => (
  <div className="absolute -left-6 sm:-left-10 bottom-10 w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-ink border border-brass/40 grid place-items-center shadow-2xl" style={{ transform: "translateZ(60px)" }}>
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full spin-slow">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <text fill="#93a8e8" style={{ fontFamily: "JetBrains Mono", fontSize: 8.4, letterSpacing: 2.4 }}>
        <textPath href="#circ">COMPLIANCE · AUDIT · ADVISORY · </textPath>
      </text>
    </svg>
    <LogoMark className="w-10 h-10 sm:w-12 sm:h-12" />
  </div>
);

const TiltFrame = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 120]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="[perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset} ref={ref}>
      <motion.div
        data-testid="hero-3d-frame"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.94, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative mx-auto w-full max-w-[460px]"
      >
        <div className="relative aspect-[4/5] rounded-t-[999px] rounded-b-[28px] overflow-hidden border border-ink/15 bg-ink-3 shadow-2xl">
          <motion.img style={{ y: imgY, scale: 1.25 }} src="/images/bse.jpg" alt="Phiroze Jeejeebhoy Towers, Mumbai" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,transparent_10%,rgba(10,20,36,0.85)_75%)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1424] via-transparent to-transparent" />
          <div className="absolute bottom-6 right-6 text-right font-mono-x text-[11px] uppercase tracking-[0.18em] text-paper/80">
            <span className="block">Dalal Street, Mumbai</span>
            <span className="block mt-1 text-[#93a8e8]">18.93° N · 72.83° E</span>
          </div>
        </div>
        <div className="absolute -right-3 sm:-right-8 top-16 rounded-2xl bg-white text-ink px-5 py-4 border border-ink/10 shadow-2xl" style={{ transform: "translateZ(90px)" }}>
          <p className="font-mono-x text-[10px] uppercase tracking-widest text-ink/60" data-testid="inspection-status-label">Inspection status</p>
          <p className="mt-1 font-display font-extrabold text-xl flex items-center gap-2" data-testid="inspection-status-value">
            <span className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse" /> Complied
          </p>
        </div>
        <SealBadge />
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section data-testid="home-hero" className="relative bg-paper text-ink overflow-hidden min-h-[100svh] flex flex-col">
    <div className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[radial-gradient(circle,rgba(29,63,168,0.10),transparent_60%)]" />
    <div className="relative flex-1 mx-auto w-full max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-32 sm:pt-40 pb-16 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}>
          <Eyebrow>Practising Company Secretaries · Est. April 2026</Eyebrow>
        </motion.div>
        <h1 data-testid="hero-heading" className="mt-8 font-display font-extrabold tracking-[-0.04em] leading-[0.92] text-[3.1rem] sm:text-7xl lg:text-[5.6rem] xl:text-[6.4rem]">
          <MaskLines lines={["Compliance,", <>engineered for</>, <span className="font-serif-i text-[var(--brass-deep)] tracking-[-0.02em]">capital markets.</span>]} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.9 }} className="mt-8 max-w-xl text-base sm:text-lg text-ink/70 leading-relaxed">
          Regulatory advisory, audit and documentation for Stock Brokers, Depository Participants, Research Analysts, Investment Advisers, Portfolio Managers and AIFs — led by over a decade of hands-on SEBI compliance experience.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }} className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/contact" data-testid="hero-consult-button" className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink text-paper px-7 py-4 font-semibold hover:bg-brass hover:text-paper transition-colors duration-300">
            Book a consultation <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
          <Link to="/services" data-testid="hero-services-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/25 px-7 py-4 font-semibold hover:border-[var(--brass-deep)] hover:text-[var(--brass-deep)] transition-colors duration-300">
            View services
          </Link>
        </motion.div>
      </div>
      <div className="lg:col-span-5 px-6 sm:px-10 lg:px-0"><TiltFrame /></div>
    </div>
    <div className="relative border-t border-ink/10">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 sm:grid-cols-3">
        {STATS.map(([n, l], i) => (
          <motion.div key={l} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 + i * 0.1 }} className="py-6 sm:py-8 sm:pr-6 flex sm:block items-baseline gap-4 border-b sm:border-b-0 sm:border-r border-ink/10 last:border-0">
            <span className="font-display font-extrabold text-3xl sm:text-4xl text-ink">{n}</span>
            <span className="block sm:mt-2 text-sm text-ink/65">{l}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
